import { BriefcaseBusiness } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// IT Services / Web Projects / Marketing & Advertising — Contracts Page
//
// Contract management with billing schedules, revenue recognition config,
// proposal linkage, and project/campaign creation
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FileText, Plus, Edit, Eye, Check, X, DollarSign, Link2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Modal } from '@/shared/components/ui/Modal';
import { formatINR } from '@/shared/utils/currency';
import { ROUTES } from '@/routes/paths';
import {
  useContracts,
  useContractMutations,
  useProposals,
} from '../hooks/useServicesProjectsApi';
import { localToday } from '@/shared/utils/utils';

const CONTRACT_TYPES = [
  { value: 'fixed_fee', label: 'Fixed Fee' },
  { value: 'time_material', label: 'Time & Material' },
  { value: 'retainer', label: 'Retainer' },
  { value: 'performance', label: 'Performance Based' },
];

const BILLING_METHODS = [
  { value: 'milestone', label: 'Milestone Based' },
  { value: 'time_material', label: 'Time & Material' },
  { value: 'retainer_monthly', label: 'Monthly Retainer' },
  { value: 'performance', label: 'Performance Based' },
];

const REVENUE_RECOGNITION_METHODS = [
  { value: 'percentage_of_completion', label: 'Percentage of Completion' },
  { value: 'milestone_signoff', label: 'Milestone Sign-off' },
  { value: 'straight_line_monthly', label: 'Straight Line Monthly' },
  { value: 'manual', label: 'Manual' },
];

const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  { value: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-amber-100 text-amber-800' },
  { value: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
];

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}

export function ContractsPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    proposalId: '',
    clientId: '',
    title: '',
    type: 'fixed_fee',
    billingMethod: 'milestone',
    totalValueMinor: 0,
    currency: 'INR',
    startDate: localToday(),
    endDate: '',
    billingSchedule: [] as any[],
    revenueRecognitionMethod: 'milestone_signoff',
    deferredRevenueAccountId: '',
    earnedRevenueAccountId: '',
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'schedule' | 'recognition'>('details');

  const { data: contracts = [], isLoading } = useContracts();
  const { data: proposals = [] } = useProposals({ status: 'accepted' });
  const { update, activate } = useContractMutations();

  const acceptedProposals = useMemo(() => proposals.filter(p => p.status === 'accepted' && !p.contractId), [proposals]);

  const resetForm = useCallback(() => {
    setFormData({
      proposalId: '',
      clientId: '',
      title: '',
      type: 'fixed_fee',
      billingMethod: 'milestone',
      totalValueMinor: 0,
      currency: 'INR',
      startDate: localToday(),
      endDate: '',
      billingSchedule: [],
      revenueRecognitionMethod: 'milestone_signoff',
      deferredRevenueAccountId: '',
      earnedRevenueAccountId: '',
    });
    setEditingId(null);
    setViewingId(null);
    setCreating(false);
    setActiveTab('details');
  }, []);

  const handleCreate = () => {
    resetForm();
    setCreating(true);
  };

  const handleView = (c: any) => {
    setViewingId(c._id);
    setFormData({
      proposalId: c.proposalId || '',
      clientId: c.clientId || '',
      title: c.title,
      type: c.type,
      billingMethod: c.billingMethod,
      totalValueMinor: c.totalValueMinor,
      currency: c.currency,
      startDate: c.startDate ? new Date(c.startDate).toISOString().split('T')[0] : '',
      endDate: c.endDate ? new Date(c.endDate).toISOString().split('T')[0] : '',
      billingSchedule: (c.billingSchedule || []).map((b: any) => ({
        key: b._id || Math.random().toString(36).slice(2),
        sequence: b.sequence,
        name: b.name,
        type: b.type,
        amountMinor: b.amountMinor,
        percentage: b.percentage,
        trigger: b.trigger,
        triggerDate: b.triggerDate ? new Date(b.triggerDate).toISOString().split('T')[0] : '',
        triggerMilestoneId: b.triggerMilestoneId,
        status: b.status,
      })),
      revenueRecognitionMethod: c.revenueRecognitionMethod,
      deferredRevenueAccountId: c.deferredRevenueAccountId || '',
      earnedRevenueAccountId: c.earnedRevenueAccountId || '',
    });
    setCreating(true);
  };

  const handleEdit = (c: any) => {
    setEditingId(c._id);
    handleView(c);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      return toast.error('Title is required');
    }
    if (!formData.proposalId && !formData.clientId) {
      return toast.error('Select a proposal or enter a client');
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        totalValueMinor: formData.totalValueMinor,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
        billingSchedule: formData.billingSchedule.map(({ key: _k, ...b }) => b),
      };

      if (editingId) {
        await update.mutateAsync({ id: editingId, payload });
        toast.success('Contract updated');
      } else {
        // For new contracts, we'd need a create endpoint
        toast.error('Create new contract from accepted proposal');
      }
      resetForm();
    } catch (err) {
      toast.error(errorText(err, 'Failed to save contract'));
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id: string, number: string) => {
    if (!window.confirm(`Activate contract ${number}?`)) return;
    try {
      await activate.mutateAsync(id);
      toast.success('Contract activated');
    } catch (err) {
      toast.error(errorText(err, 'Failed to activate contract'));
    }
  };

  const addScheduleItem = () =>
    setFormData(f => ({ ...f, billingSchedule: [...f.billingSchedule, {
      key: Math.random().toString(36).slice(2),
      sequence: f.billingSchedule.length + 1,
      name: '',
      type: 'milestone',
      amountMinor: 0,
      percentage: 0,
      trigger: 'milestone_acceptance',
      triggerDate: '',
      triggerMilestoneId: '',
      status: 'pending',
    }] }));

  const removeScheduleItem = (key: string) =>
    setFormData(f => ({ ...f, billingSchedule: f.billingSchedule.filter(b => b.key !== key).map((b, i) => ({ ...b, sequence: i + 1 })) }));

  const setScheduleItem = (key: string, patch: any) =>
    setFormData(f => ({ ...f, billingSchedule: f.billingSchedule.map(b => b.key === key ? { ...b, ...patch } : b) }));

  if (isLoading) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="space-y-6 pb-16">
      <PageHeader
        icon={BriefcaseBusiness}
        eyebrow="Projects & Services"
        title="Contracts"
        description={<>Manage contracts with billing schedules, revenue recognition, and project linkage.</>}
        actions={
          <><Button onClick={handleCreate}>
                  <Plus className="h-4 w-4 mr-1" /> New Contract
                </Button></>
        }
      />

      {contracts.length === 0 ? (
        <EmptyState
          title="No contracts yet"
          description="Create contracts from accepted proposals to define billing schedules and revenue recognition."
          action={<Button onClick={handleCreate}>Create Contract</Button>}
        />
      ) : (
        <div className="space-y-3">
          {contracts.map((c) => (
            <div key={c._id} className="bg-card border border-border rounded-lg p-4 flex flex-wrap items-center gap-3">
              <FileText className="h-4 w-4 text-blue-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{c.contractNumber || c._id.slice(-6)}</span>
                  <Badge className={STATUSES.find(s => s.value === c.status)?.color || 'bg-slate-100 text-slate-700'}>{c.status}</Badge>
                  <Badge className="bg-blue-100 text-blue-800">{c.type.replace('_', ' ')}</Badge>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {c.clientName || 'No client'} · {c.billingMethod.replace('_', ' ')} · {formatINR(c.totalValueMinor || 0)}
                  {c.projectId && (
                    <> <span className="mx-1">→</span> <span className="text-blue-500">Project linked</span> </>
                  )}
                </p>
              </div>
              <div className="text-right shrink-0">
                <div className="font-bold text-sm">{formatINR(c.totalValueMinor || 0)}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => handleView(c)} title="View">
                  <Eye className="h-4 w-4" />
                </Button>
                {c.status === 'draft' && (
                  <Button variant="secondary" size="sm" onClick={() => handleActivate(c._id, c.contractNumber)}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Activate
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={() => handleEdit(c)} title="Edit">
                  <Edit className="h-4 w-4" />
                </Button>
                {c.proposalId && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-blue-500 hover:text-blue-600"
                    title="View Proposal"
                    onClick={() => navigate(`${ROUTES.svcProposals}?proposalId=${encodeURIComponent(c.proposalId)}`)}
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal open={creating} onClose={resetForm} title={editingId ? 'Edit Contract' : viewingId ? 'Contract Details' : 'Create Contract'} size="xl">
        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          {/* Tab Navigation */}
          <div className="flex border-b border-border">
            {(['details', 'schedule', 'recognition'] as const).map(tab => (
              <Button
                key={tab}
                type="button"
                variant={activeTab === tab ? 'secondary' : 'ghost'}
                size="sm"
                className="px-3 py-2"
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </Button>
            ))}
          </div>

          <div className="space-y-5">
            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Proposal (Accepted)</label>
                    <Select
                      value={formData.proposalId}
                      placeholder="Select accepted proposal"
                      options={acceptedProposals.map(p => ({ value: p._id, label: `${p.proposalNumber} — ${p.clientName} (${formatINR(p.totalAmountMinor)})` }))}
                      onChange={e => {
                        const sel = acceptedProposals.find(p => p._id === e.target.value);
                        if (sel) {
                          setFormData(fd => ({
                            ...fd,
                            proposalId: sel._id,
                            clientId: sel.clientId,
                            title: sel.title,
                            totalValueMinor: sel.totalAmountMinor,
                            billingSchedule: (sel.milestones || []).map((m: any, i: number) => ({
                              key: m._id || Math.random().toString(36).slice(2),
                              sequence: i + 1,
                              name: m.name,
                              type: 'milestone',
                              amountMinor: m.amountMinor,
                              percentage: m.percentage,
                              trigger: 'milestone_acceptance',
                              triggerDate: m.dueDate ? new Date(m.dueDate).toISOString().split('T')[0] : '',
                              triggerMilestoneId: m._id,
                              status: 'pending',
                            })),
                          }));
                        } else {
                          setFormData(fd => ({ ...fd, proposalId: e.target.value }));
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Contract Type</label>
                    <Select
                      value={formData.type}
                      options={CONTRACT_TYPES}
                      onChange={e => setFormData({ ...formData, type: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Title *</label>
                    <Input
                      placeholder="Website Development Contract"
                      value={formData.title}
                      onChange={e => setFormData({ ...formData, title: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Billing Method</label>
                    <Select
                      value={formData.billingMethod}
                      options={BILLING_METHODS}
                      onChange={e => setFormData({ ...formData, billingMethod: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Total Value (₹)</label>
                    <Input
                      type="number"
                      min="0"
                      step="100"
                      placeholder="50000000"
                      value={formData.totalValueMinor}
                      onChange={e => setFormData({ ...formData, totalValueMinor: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Start Date *</label>
                    <Input
                      type="date"
                      value={formData.startDate}
                      onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">End Date</label>
                    <Input
                      type="date"
                      value={formData.endDate}
                      onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Billing Schedule Tab */}
            {activeTab === 'schedule' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-semibold text-slate-600">Billing Schedule</span>
                  </div>
                  <Button type="button" variant="secondary" size="sm" onClick={addScheduleItem}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
                  </Button>
                </div>

                <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800">
                      <tr>
                        {['#', 'Name', 'Type', 'Amount (₹)', '%', 'Trigger', 'Trigger Date', 'Milestone', 'Status', ''].map(h => (
                          <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {formData.billingSchedule.map((b, i) => (
                        <tr key={b.key} className="bg-white dark:bg-slate-900">
                          <td className="px-4 py-3 w-8 text-center">{b.sequence || i + 1}</td>
                          <td className="px-4 py-3 w-36">
                            <Input
                              placeholder="Item name"
                              value={b.name}
                              onChange={e => setScheduleItem(b.key, { name: e.target.value })}
                            />
                          </td>
                          <td className="px-4 py-3 w-32">
                            <Select
                              value={b.type}
                              options={BILLING_METHODS}
                              onChange={e => setScheduleItem(b.key, { type: e.target.value })}
                            />
                          </td>
                          <td className="px-4 py-3 w-32">
                            <Input
                              type="number"
                              min="0"
                              step="100"
                              placeholder="0"
                              value={b.amountMinor}
                              onChange={e => setScheduleItem(b.key, { amountMinor: parseInt(e.target.value) || 0 })}
                            />
                          </td>
                          <td className="px-4 py-3 w-16">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              placeholder="%"
                              value={b.percentage}
                              onChange={e => setScheduleItem(b.key, { percentage: parseInt(e.target.value) || 0 })}
                            />
                          </td>
                          <td className="px-4 py-3 w-36">
                            <Select
                              value={b.trigger}
                              options={[
                                { value: 'date', label: 'Fixed Date' },
                                { value: 'milestone_acceptance', label: 'Milestone Acceptance' },
                                { value: 'monthly', label: 'Monthly' },
                                { value: 'quarterly', label: 'Quarterly' },
                              ]}
                              onChange={e => setScheduleItem(b.key, { trigger: e.target.value })}
                            />
                          </td>
                          <td className="px-4 py-3 w-28">
                            <Input
                              type="date"
                              value={b.triggerDate}
                              onChange={e => setScheduleItem(b.key, { triggerDate: e.target.value })}
                            />
                          </td>
                          <td className="px-4 py-3 w-32">
                             <Select
                               value={b.triggerMilestoneId}
                               placeholder="Select milestone"
                               options={[
                                 { value: '', label: '--' },
                                 ...(formData.billingSchedule || [])
                                   .filter((x: any) => x.type === 'milestone')
                                   .map((m: any) => ({ value: m.key, label: `${m.sequence}. ${m.name}` }))
                               ]}
                               onChange={e => setScheduleItem(b.key, { triggerMilestoneId: e.target.value })}
                             />
                          </td>
                          <td className="px-4 py-3 w-24">
                            <Badge className={b.status === 'paid' ? 'bg-emerald-100 text-emerald-800' : b.status === 'invoiced' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'}>
                              {b.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 w-10 text-right">
                            <Button variant="ghost" size="icon" onClick={() => removeScheduleItem(b.key)} disabled={formData.billingSchedule.length <= 1} className="text-destructive">
                              <X className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Revenue Recognition Tab */}
            {activeTab === 'recognition' && (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Recognition Method</label>
                    <Select
                      value={formData.revenueRecognitionMethod}
                      options={REVENUE_RECOGNITION_METHODS}
                      onChange={e => setFormData({ ...formData, revenueRecognitionMethod: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Deferred Revenue Account</label>
                    <Input placeholder="Account ID" value={formData.deferredRevenueAccountId} onChange={e => setFormData({ ...formData, deferredRevenueAccountId: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Earned Revenue Account</label>
                    <Input placeholder="Account ID" value={formData.earnedRevenueAccountId} onChange={e => setFormData({ ...formData, earnedRevenueAccountId: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Currency</label>
                    <Select
                      value={formData.currency}
                      options={['INR', 'USD', 'EUR', 'GBP', 'AED'].map(c => ({ value: c, label: c }))}
                      onChange={e => setFormData({ ...formData, currency: e.target.value })}
                    />
                  </div>
                </div>

                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 text-sm">
                  <p className="font-medium text-slate-700 dark:text-slate-300 mb-2">Revenue Recognition Schedule</p>
                  <p className="text-slate-600 dark:text-slate-400">
                    Schedule will be auto-generated from billing schedule upon contract activation.
                    Each billing item creates a recognition entry with the same amount and trigger date.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-border pt-4 mt-2">
            <Button type="button" variant="outline" onClick={resetForm} className="px-5 py-2">Cancel</Button>
            {(!editingId && !viewingId) && (
              <Button type="submit" loading={saving} className="px-5 py-2" disabled={!formData.proposalId}>
                Create Contract
              </Button>
            )}
            {editingId && (
              <Button type="submit" loading={saving} className="px-5 py-2">
                Update Contract
              </Button>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
}
